import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface SandstormEffectProps {
  intensity?: number;      // 0-1
  particleCount?: number;
  areaSize?: [number, number]; // width, depth
  color?: string;
  windDirection?: [number, number, number];
}

export function SandstormEffect({
  intensity = 0.5,
  particleCount = 800,
  areaSize = [25, 25],
  color = '#d4a574',
  windDirection = [1, 0, 0.3],
}: SandstormEffectProps) {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const particlesRef = useRef<{
    position: THREE.Vector3;
    velocity: THREE.Vector3;
    scale: number;
    turbulence: number;
  }[]>([]);

  const actualCount = Math.floor(particleCount * intensity);

  // Normalize wind direction
  const windVec = useMemo(() => {
    const vec = new THREE.Vector3(...windDirection).normalize();
    return vec.multiplyScalar(3 + intensity * 4); // Speed based on intensity
  }, [windDirection, intensity]);

  // Initialize sand particles
  useMemo(() => {
    particlesRef.current = [];

    for (let i = 0; i < actualCount; i++) {
      const height = Math.random() * 4; // Sand stays low to the ground
      particlesRef.current.push({
        position: new THREE.Vector3(
          (Math.random() - 0.5) * areaSize[0],
          height,
          (Math.random() - 0.5) * areaSize[1]
        ),
        velocity: new THREE.Vector3(
          windVec.x * (0.8 + Math.random() * 0.4),
          (Math.random() - 0.5) * 0.5,
          windVec.z * (0.8 + Math.random() * 0.4)
        ),
        scale: 0.02 + Math.random() * 0.03,
        turbulence: Math.random() * Math.PI * 2,
      });
    }
  }, [actualCount, areaSize[0], areaSize[1], windVec]);

  useFrame((state, delta) => {
    if (!meshRef.current) return;

    const time = state.clock.elapsedTime;
    const [width, depth] = areaSize;
    const matrix = new THREE.Matrix4();

    for (let i = 0; i < particlesRef.current.length; i++) {
      const particle = particlesRef.current[i];

      // Turbulent motion
      const turbX = Math.sin(time * 3 + particle.turbulence) * 0.5;
      const turbY = Math.sin(time * 2 + particle.turbulence * 1.5) * 0.3;
      const turbZ = Math.cos(time * 2.5 + particle.turbulence) * 0.5;

      // Update position
      particle.position.x += (particle.velocity.x + turbX) * delta;
      particle.position.y += (particle.velocity.y + turbY) * delta;
      particle.position.z += (particle.velocity.z + turbZ) * delta;

      // Keep sand low to the ground (0.1 to 5 units high)
      if (particle.position.y < 0.1) {
        particle.position.y = 0.1;
        particle.velocity.y = Math.abs(particle.velocity.y) * 0.5;
      }
      if (particle.position.y > 5) {
        particle.position.y = 5;
        particle.velocity.y = -Math.abs(particle.velocity.y) * 0.5;
      }

      // Wrap around - respawn on opposite side
      if (particle.position.x > width / 2) {
        particle.position.x = -width / 2;
        particle.position.y = Math.random() * 4;
      }
      if (particle.position.x < -width / 2) {
        particle.position.x = width / 2;
        particle.position.y = Math.random() * 4;
      }
      if (particle.position.z > depth / 2) {
        particle.position.z = -depth / 2;
      }
      if (particle.position.z < -depth / 2) {
        particle.position.z = depth / 2;
      }

      // Build transformation matrix
      matrix.setPosition(particle.position);
      matrix.scale(new THREE.Vector3(particle.scale, particle.scale, particle.scale));

      meshRef.current!.setMatrixAt(i, matrix);
    }

    meshRef.current.instanceMatrix.needsUpdate = true;
  });

  if (actualCount === 0) return null;

  return (
    <group>
      {/* Sand particles */}
      <instancedMesh ref={meshRef} args={[undefined, undefined, actualCount]}>
        <sphereGeometry args={[1, 4, 4]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.5 * intensity}
        />
      </instancedMesh>

      {/* Atmospheric dust fog effect */}
      <mesh position={[0, 2, 0]}>
        <boxGeometry args={[areaSize[0], 6, areaSize[1]]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.1 * intensity}
          side={THREE.BackSide}
        />
      </mesh>
    </group>
  );
}
